import React, { useState, useEffect } from 'react';
import { whatsappTemplateService, metaWhatsappPhoneNumberService } from '../../services/api';

const WhatsAppTemplateSection = () => {
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // WABA ID management
  const [wabaIds, setWabaIds] = useState([]);
  const [selectedWabaId, setSelectedWabaId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'create', or 'templates'

  // Template listing
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedWabaIdForTemplates, setSelectedWabaIdForTemplates] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    language: '',
    waba_id: '',
    from_phone_number: '',
    components: [
      {
        type: 'HEADER',
        format: 'TEXT',
        text: ''
      },
      {
        type: 'BODY',
        text: ''
      }
    ]
  });

  // Load categories and languages on component mount
  useEffect(() => {
    fetchCategoriesAndLanguages();
    fetchWabaIds();
  }, []);

  const fetchCategoriesAndLanguages = async () => {
    try {
      const [categoriesData, languagesData] = await Promise.all([
        whatsappTemplateService.getCategories(),
        whatsappTemplateService.getLanguages()
      ]);
      setCategories(categoriesData.categories);
      setLanguages(languagesData.languages);
    } catch (err) {
      console.error('Error fetching template data:', err);
      setError('Failed to load template categories and languages.');
    }
  };

  const fetchWabaIds = async () => {
    try {
      const phoneNumbers = await metaWhatsappPhoneNumberService.getAllPhoneNumbers();

      // Group by WABA ID
      const groupedWabaIds = phoneNumbers.reduce((acc, phoneNumber) => {
        const wabaId = phoneNumber.waba_id;
        if (!acc[wabaId]) {
          acc[wabaId] = {
            waba_id: wabaId,
            phone_numbers: [],
            total_phone_numbers: 0
          };
        }
        acc[wabaId].phone_numbers.push(phoneNumber);
        acc[wabaId].total_phone_numbers += 1;
        return acc;
      }, {});

      setWabaIds(Object.values(groupedWabaIds));
    } catch (err) {
      console.error('Error fetching WABA IDs:', err);
      setError('Failed to load WABA IDs.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleComponentChange = (index, field, value) => {
    const newComponents = [...formData.components];
    newComponents[index] = {
      ...newComponents[index],
      [field]: value
    };
    setFormData({
      ...formData,
      components: newComponents
    });
  };

  const addComponent = () => {
    setFormData({
      ...formData,
      components: [
        ...formData.components,
        {
          type: 'BODY',
          text: ''
        }
      ]
    });
  };

  const removeComponent = (index) => {
    if (formData.components.length > 1) {
      const newComponents = formData.components.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        components: newComponents
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Filter out empty components
      const filteredComponents = formData.components.filter(comp =>
        comp.text && comp.text.trim() !== ''
      );

      if (filteredComponents.length === 0) {
        throw new Error('At least one component must have content');
      }

      const templateData = {
        ...formData,
        components: filteredComponents
      };

      const result = await whatsappTemplateService.createTemplate(templateData);

      setSuccess(`Template created successfully! ID: ${result.id}`);

      // Reset form
      setFormData({
        name: '',
        category: '',
        language: '',
        waba_id: '',
        from_phone_number: '',
        components: [
          {
            type: 'HEADER',
            format: 'TEXT',
            text: ''
          },
          {
            type: 'BODY',
            text: ''
          }
        ]
      });
    } catch (err) {
      console.error('Error creating template:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const getComponentTypeLabel = (type) => {
    const labels = {
      'HEADER': 'Header',
      'BODY': 'Body',
      'FOOTER': 'Footer',
      'BUTTONS': 'Buttons'
    };
    return labels[type] || type;
  };

  const handleWabaIdSelect = (wabaId) => {
    setSelectedWabaId(wabaId);
    setFormData(prev => ({ ...prev, waba_id: wabaId }));
    setViewMode('create');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedWabaId(null);
    setFormData({
      name: '',
      category: '',
      language: '',
      waba_id: '',
      from_phone_number: '',
      components: [
        {
          type: 'HEADER',
          format: 'TEXT',
          text: ''
        },
        {
          type: 'BODY',
          text: ''
        }
      ]
    });
  };

  const handleListTemplates = async (wabaId) => {
    setSelectedWabaIdForTemplates(wabaId);
    setTemplatesLoading(true);
    setError(null);

    try {
      const result = await whatsappTemplateService.listTemplates(wabaId);
      setTemplates(result.data || []);
      setViewMode('templates');
    } catch (err) {
      console.error('Error listing templates:', err);
      setError('Failed to load templates for this WABA ID.');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleBackFromTemplates = () => {
    setViewMode('list');
    setSelectedWabaIdForTemplates(null);
    setTemplates([]);
  };

  const handleApplyAllNexiTemplates = async (wabaId) => {
    if (!window.confirm(`Are you sure you want to apply all Nexi templates to WABA ID: ${wabaId}? This will create multiple templates.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await whatsappTemplateService.applyAllNexiTemplates(wabaId);
      setSuccess(`Successfully applied all Nexi templates to WABA ID: ${wabaId}`);

      // Refresh the templates list if we're currently viewing templates for this WABA ID
      if (selectedWabaIdForTemplates === wabaId) {
        await handleListTemplates(wabaId);
      }
    } catch (err) {
      console.error('Error applying Nexi templates:', err);
      setError(`Failed to apply Nexi templates: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {viewMode === 'list' ? (
        // WABA ID List View
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">WhatsApp Business Accounts</h2>
            <button
              onClick={() => setViewMode('create')}
              className="px-4 py-2 bg-[#075e54] text-white rounded-md hover:bg-[#054d44] focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:ring-offset-2"
            >
              Create Template
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {wabaIds.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No WhatsApp Business Accounts found.</p>
              <p className="text-sm text-gray-400 mt-2">Add phone numbers in the Meta WhatsApp Phone Numbers section first.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {wabaIds.map((wabaGroup) => (
                <div key={wabaGroup.waba_id} className="border border-gray-200 rounded-lg p-4 hover:border-[#075e54] transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        WABA ID: {wabaGroup.waba_id}
                      </h3>
                      <div className="text-sm text-gray-600">
                        <p>Phone Numbers: {wabaGroup.total_phone_numbers}</p>
                        <div className="mt-2 space-y-1">
                          {wabaGroup.phone_numbers.map((phoneNumber) => (
                            <div key={phoneNumber.id} className="flex items-center space-x-2">
                              <span className="text-gray-500">•</span>
                              <span>{phoneNumber.name}</span>
                              <span className="text-gray-400">({phoneNumber.phone_number})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleListTemplates(wabaGroup.waba_id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        List Templates
                      </button>
                      <button
                        onClick={() => handleWabaIdSelect(wabaGroup.waba_id)}
                        className="px-4 py-2 bg-[#075e54] text-white rounded-md hover:bg-[#054d44] focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:ring-offset-2"
                      >
                        Create Template
                      </button>
                      <button
                        onClick={() => handleApplyAllNexiTemplates(wabaGroup.waba_id)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      >
                        Apply Nexi Templates
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : viewMode === 'create' ? (
        // Template Creation View
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Create WhatsApp Template</h2>
              {selectedWabaId && (
                <p className="text-sm text-gray-600 mt-1">For WABA ID: {selectedWabaId}</p>
              )}
            </div>
            <button
              onClick={handleBackToList}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              ← Back to WABA IDs
            </button>
          </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Template Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter template name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                <option value="">Select category</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language *
              </label>
              <select
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              >
                <option value="">Select language</option>
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WABA ID
              </label>
              <input
                type="text"
                name="waba_id"
                value={formData.waba_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter WABA ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Phone Number
              </label>
              <input
                type="text"
                name="from_phone_number"
                value={formData.from_phone_number}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          {/* Template Components */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-medium text-gray-900">Template Components</h3>
              <button
                type="button"
                onClick={addComponent}
                className="px-3 py-1 text-sm bg-[#075e54] text-white rounded-md hover:bg-[#054d44] focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:ring-offset-2"
              >
                Add Component
              </button>
            </div>

            <div className="space-y-4">
              {formData.components.map((component, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <select
                        value={component.type}
                        onChange={(e) => handleComponentChange(index, 'type', e.target.value)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                      >
                        <option value="HEADER">Header</option>
                        <option value="BODY">Body</option>
                        <option value="FOOTER">Footer</option>
                      </select>

                      {component.type === 'HEADER' && (
                        <select
                          value={component.format || 'TEXT'}
                          onChange={(e) => handleComponentChange(index, 'format', e.target.value)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                        >
                          <option value="TEXT">Text</option>
                          <option value="IMAGE">Image</option>
                          <option value="VIDEO">Video</option>
                          <option value="DOCUMENT">Document</option>
                        </select>
                      )}
                    </div>

                    {formData.components.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeComponent(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <textarea
                    value={component.text}
                    onChange={(e) => handleComponentChange(index, 'text', e.target.value)}
                    placeholder={`Enter ${getComponentTypeLabel(component.type).toLowerCase()} text...`}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-[#075e54] text-white rounded-md hover:bg-[#054d44] focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
        ) : viewMode === 'templates' ? (
        // Templates List View
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Existing Templates</h2>
              {selectedWabaIdForTemplates && (
                <p className="text-sm text-gray-600 mt-1">For WABA ID: {selectedWabaIdForTemplates}</p>
              )}
            </div>
            <button
              onClick={handleBackFromTemplates}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              ← Back to WABA IDs
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {templatesLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No templates found for this WABA ID.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {templates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-6">
                  {/* Template Header Info */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        template.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                        template.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        template.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {template.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      ID: {template.id}
                    </div>
                  </div>

                  {/* Template Meta Info */}
                  <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
                    <span><strong>Category:</strong> {template.category}</span>
                    <span><strong>Language:</strong> {template.language}</span>
                  </div>

                  {/* WhatsApp-like Template Preview */}
                  {template.components && template.components.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="text-xs text-gray-500 mb-2 font-medium">TEMPLATE PREVIEW</div>

                      {/* Template Content */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 max-w-sm">
                        {/* Header Component */}
                        {template.components.find(comp => comp.type === 'HEADER') && (
                          <div className="mb-3">
                            {template.components.find(comp => comp.type === 'HEADER')?.format === 'IMAGE' && (
                              <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center mb-2">
                                <div className="text-gray-500 text-sm">📷 Image</div>
                              </div>
                            )}
                            {template.components.find(comp => comp.type === 'HEADER')?.format === 'VIDEO' && (
                              <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center mb-2">
                                <div className="text-gray-500 text-sm">🎥 Video</div>
                              </div>
                            )}
                            {template.components.find(comp => comp.type === 'HEADER')?.format === 'DOCUMENT' && (
                              <div className="w-full h-16 bg-gray-200 rounded-lg flex items-center justify-center mb-2">
                                <div className="text-gray-500 text-sm">📄 Document</div>
                              </div>
                            )}
                            {template.components.find(comp => comp.type === 'HEADER')?.text && (
                              <div className="text-lg font-semibold text-gray-900 mb-2 whitespace-pre-wrap">
                                {template.components.find(comp => comp.type === 'HEADER')?.text}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Body Component */}
                        {template.components.find(comp => comp.type === 'BODY') && (
                          <div className="mb-3">
                            <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                              {template.components.find(comp => comp.type === 'BODY')?.text}
                            </div>
                          </div>
                        )}

                        {/* Footer Component */}
                        {template.components.find(comp => comp.type === 'FOOTER') && (
                          <div className="mb-3">
                            <div className="text-sm text-gray-600 whitespace-pre-wrap">
                              {template.components.find(comp => comp.type === 'FOOTER')?.text}
                            </div>
                          </div>
                        )}

                        {/* Buttons */}
                        {template.components.find(comp => comp.type === 'BUTTONS') && (
                          <div className="space-y-2">
                            {template.components.find(comp => comp.type === 'BUTTONS')?.buttons?.map((button, btnIndex) => (
                              <div key={btnIndex} className="w-full">
                                {button.type === 'QUICK_REPLY' && (
                                  <button className="w-full py-2 px-4 bg-[#075e54] text-white text-sm rounded-lg hover:bg-[#054d44] transition-colors">
                                    {button.text}
                                  </button>
                                )}
                                {button.type === 'URL' && (
                                  <button className="w-full py-2 px-4 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                                    🔗 {button.text}
                                  </button>
                                )}
                                {button.type === 'PHONE_NUMBER' && (
                                  <button className="w-full py-2 px-4 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors">
                                    📞 {button.text}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Component Details */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-xs text-gray-500 mb-2 font-medium">COMPONENT DETAILS</div>
                        <div className="space-y-2">
                          {template.components.map((component, index) => (
                            <div key={index} className="flex items-start space-x-2 text-xs">
                              <span className="w-16 text-gray-500 font-medium">{component.type}:</span>
                              <span className="text-gray-700 flex-1">
                                {component.text ? (
                                  component.text.length > 100 ? (
                                    <div className="whitespace-pre-wrap">
                                      {component.text.substring(0, 100)}...
                                    </div>
                                  ) : (
                                    <div className="whitespace-pre-wrap">
                                      {component.text}
                                    </div>
                                  )
                                ) : (
                                  component.format ? `${component.format} format` : 'No content'
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        ) : null}

      {/* Information Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">About WhatsApp Templates</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Templates must be approved by WhatsApp before use</li>
          <li>• Header components support text, image, video, and document formats</li>
          <li>• Body text can include variables using curly braces syntax (e.g., 1, 2)</li>
          <li>• Templates are categorized for business use cases</li>
          <li>• Language selection affects template approval process</li>
          <li>• Check the example file: <code className="bg-gray-100 px-1 rounded">waba_template_create.json</code></li>
        </ul>
      </div>
    </div>
  );
};

export default WhatsAppTemplateSection;
